// ---------------------------------------------------------------------------
// roadResolve — turn a point into a ROAD, or admit we can't and ask.
//
// R1 invariant: no new road contribution may be created from a passive raw GPS
// point. Everything here exists to make that true. A contribution either
// resolves to a real OSM way (and is stored at a point derived from that way's
// geometry), or it goes to the picker, or it is queued unresolved. It never
// falls back to the submitter's coordinate.
//
// Nothing in this module is new infrastructure:
//   • the roads come from the already-deployed `kind: "snaproads"` Overpass
//     query (functions/overpass/index.ts) — access=private|no filtered server-side
//   • mode-legality and calm-class weighting follow lib/loopSnap.ts
//   • the identity string is polylineSegmentKey() from api/roadSegments.ts,
//     the same `pl:` scheme R0 recovered and R3 will migrate to Road Index ids
//
// What IS new is edge projection (loopSnap snaps to the nearest vertex, which
// on a long straight way can be 200 m out while the road is 5 m away) and
// grouping — see GROUPING below.
// ---------------------------------------------------------------------------
import type { LngLat } from "@/utils/polyline";
import { polylineSegmentKey } from "@/api/roadSegments";
import { CLASS_WEIGHT } from "@/lib/loopSnap";

export type RoadMode = "driving" | "walking" | "biking";

/** Raw Overpass way, as `kind: "snaproads"` returns it (`out tags geom`). */
export interface OverpassWay {
  type?: string;
  id?: number;
  tags?: Record<string, string>;
  geometry?: Array<{ lat: number; lon: number }>;
}

export interface RoadCandidate {
  /** Stable within one resolve() call; used as a React key and in telemetry. */
  groupId: string;
  /** What the picker shows. Never empty. */
  displayName: string;
  /** The road's own name/ref, or null when OSM genuinely has none. */
  label: string | null;
  highway: string;
  /** Every OSM way id in this group — carried so R3 can re-resolve to Road Index. */
  wayIds: number[];
  /** Perpendicular distance from the query point to the nearest edge, metres. */
  distM: number;
  /** The matched edge. */
  edgeA: LngLat;
  edgeB: LngLat;
  /** Point on the matched edge nearest the query point. */
  snapped: LngLat;
  /** Midpoint of the matched edge — the PUBLIC location for this contribution. */
  representative: LngLat;
  /** `pl:` identity for the matched edge. */
  segmentRef: string;
  /** Bearing of the matched edge, 0-360. Used to disambiguate unnamed roads. */
  bearingDeg: number;
  /** Geometry of the group member that owned the matched edge. The picker
   *  hands this to requestMapHighlight so the user sees the road, not a name. */
  geometry: LngLat[];
  /** distM x CLASS_WEIGHT — the ranking value, not a distance. */
  score: number;
}

export type AmbiguityReason = "accuracy" | "distance" | "separation";

export type RoadResolution =
  | { status: "confident"; chosen: RoadCandidate; candidates: RoadCandidate[]; accuracyM: number | null }
  | { status: "ambiguous"; reason: AmbiguityReason; candidates: RoadCandidate[]; accuracyM: number | null }
  | { status: "no_data"; reason: "empty" | "offline" | "error" };

// --- the three gates -------------------------------------------------------
/** Max reported GPS accuracy for a fix we will resolve without asking. */
export const MAX_CONTRIB_ACCURACY_M = 25;
/** Max perpendicular distance to call a road "the one you're on". */
export const MAX_SNAP_DIST_M = 25;
/** Runner-up must be this many times farther... */
export const SEPARATION_RATIO = 2;
/** ...or this many metres clear, whichever is kinder to the user. */
export const SEPARATION_ABS_M = 30;

/** Radius of the Overpass tile. Tighter than loopSnap's 800 m: we want
 *  "roads you could plausibly be on", not "roads within a walk". */
const TILE_PAD_DEG = 0.00225; // ~250 m
/** Beyond this a way is not even offered in the picker. Matched to the tile
 *  radius on purpose: someone standing in a car park or a field may be 200 m
 *  from the road they mean, and the picker has to be able to offer it. The
 *  confidence gates still refuse to choose it for them — see MAX_SNAP_DIST_M. */
const MAX_CANDIDATE_DIST_M = 250;
/** How many groups the picker shows. */
export const MAX_PICKER_CANDIDATES = 4;

// ---------------------------------------------------------------------------
// Rateable classes
//
// loopSnap.DRIVE_CLASSES deliberately omits motorway — a loop should never aim
// at one. Rating is the opposite case: "I 5 South (Gorgeous) — 3/3" is a real
// row in community_tags and must stay possible. So driving gains motorway here.
//
// service/driveway/parking_aisle are excluded outright, for every mode. Those
// are exactly where the bad pins landed, so a ROAD rating can never resolve to
// somebody's driveway or a supermarket car park.
// ---------------------------------------------------------------------------
const RATEABLE_DRIVE = new Set([
  "motorway", "motorway_link",
  "trunk", "trunk_link",
  "primary", "primary_link",
  "secondary", "secondary_link",
  "tertiary", "tertiary_link",
  "unclassified", "residential", "living_street",
]);
const WALK_EXTRA = new Set(["footway", "path", "pedestrian", "track", "cycleway", "bridleway", "steps"]);
const BIKE_EXTRA = new Set(["cycleway", "track", "path"]);
/** Never rateable as a road, whatever the mode. */
const NEVER_RATEABLE = new Set(["service", "driveway", "parking_aisle", "rest_area", "services"]);

const banned = (v?: string) => v === "no" || v === "private";

/** Is this way something a person could meaningfully rate as a road? */
export function wayRateable(tags: Record<string, string>, mode: RoadMode): boolean {
  const hw = tags.highway ?? "";
  if (!hw || NEVER_RATEABLE.has(hw)) return false;
  if (tags.service) return false; // service=driveway/parking_aisle/alley
  if (banned(tags.access)) return false;
  if (mode === "driving") {
    if (!RATEABLE_DRIVE.has(hw)) return false;
    return !(banned(tags.motor_vehicle) || banned(tags.vehicle));
  }
  if (mode === "walking") {
    if (hw.startsWith("motorway") || hw.startsWith("trunk")) return false;
    if (!RATEABLE_DRIVE.has(hw) && !WALK_EXTRA.has(hw)) return false;
    return !banned(tags.foot);
  }
  const bikeSigned = tags.bicycle === "yes" || tags.bicycle === "designated" || tags.bicycle === "permissive";
  if (hw.startsWith("motorway") || hw.startsWith("trunk")) return false;
  if (!RATEABLE_DRIVE.has(hw) && !BIKE_EXTRA.has(hw) && !bikeSigned) return false;
  return !banned(tags.bicycle);
}

// --- geometry --------------------------------------------------------------

export function metresBetween(a: LngLat, b: LngLat): number {
  const R = 6371000;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos((a[1] * Math.PI) / 180) * Math.cos((b[1] * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** True perpendicular projection onto an edge — the thing loopSnap doesn't do. */
export function projectPointToEdge(
  p: LngLat, a: LngLat, b: LngLat,
): { snapped: LngLat; distM: number; t: number } {
  const kx = Math.cos((p[1] * Math.PI) / 180);
  const ax = (a[0] - p[0]) * kx, ay = a[1] - p[1];
  const bx = (b[0] - p[0]) * kx, by = b[1] - p[1];
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 > 0 ? Math.max(0, Math.min(1, (-ax * dx - ay * dy) / len2)) : 0;
  const snapped: LngLat = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  return { snapped, distM: metresBetween(p, snapped), t };
}

/** Compass bearing a → b, degrees, 0-360. */
export function bearingDeg(a: LngLat, b: LngLat): number {
  const φ1 = (a[1] * Math.PI) / 180, φ2 = (b[1] * Math.PI) / 180;
  const Δλ = ((b[0] - a[0]) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (((Math.atan2(y, x) * 180) / Math.PI) + 360) % 360;
}

/** Smallest absolute angle between two bearings, 0-180. */
export function angleBetween(a: number, b: number): number {
  const diff = Math.abs(a - b) % 360;
  return diff > 180 ? 360 - diff : diff;
}

/** Axis label for a bearing, collapsed to a two-ended road axis. */
export function axisLabel(deg: number): string {
  const axes = ["north–south", "NNE–SSW", "NE–SW", "ENE–WSW", "east–west", "ESE–WNW", "SE–NW", "SSE–NNW"];
  const idx = Math.round((((deg % 180) + 180) % 180) / 22.5) % 8;
  return axes[idx];
}

// ---------------------------------------------------------------------------
// GROUPING
//
// OSM splits one street into many ways at every attribute change — a speed
// limit, a bridge, a surface. Two candidate ways 5 m apart are routinely the
// SAME road, so ranking raw ways would fail the separation gate constantly and
// fire the picker where there is nothing to disambiguate.
//
// Named ways group by name/ref + class, but only when physically connected (or
// within a 15 m topology gap), so two different "Church Street"s can't merge.
//
// UNNAMED ways are the case that needs care: grouping them by class alone would
// collapse every unrelated track in the tile into one candidate. So an unnamed
// way joins another only when ALL of:
//   1. same highway class, AND
//   2. they share an exact endpoint coordinate — OSM ways that connect share a
//      node, so the emitted lat/lon are identical, AND
//   3. at that node they run STRAIGHT THROUGH: the two bearings measured away
//      from the shared node are within 45° of opposite. A side road meeting at
//      ~90° is a different road and stays a different candidate.
//   4. at a node where three or more same-class unnamed ways meet, only the
//      single most-collinear pair may join, and only if the runner-up pair is
//      at least 20° worse. A genuine multi-way junction joins nothing.
//
// Two unrelated unnamed tracks that never touch therefore remain two
// candidates — the picker asks, which is the correct outcome.
// ---------------------------------------------------------------------------
const ENDPOINT_GAP_M = 15;
const COLLINEAR_TOLERANCE_DEG = 45;
const COLLINEAR_MARGIN_DEG = 20;

export interface PreparedWay {
  id: number;
  tags: Record<string, string>;
  highway: string;
  labelKey: string | null;
  label: string | null;
  coords: LngLat[];
}

function normaliseLabel(tags: Record<string, string>): { label: string | null; key: string | null } {
  const raw = (tags.name ?? tags.ref ?? "").trim().replace(/\s+/g, " ");
  if (!raw) return { label: null, key: null };
  return { label: raw, key: raw.toLowerCase() };
}

const nodeKey = (p: LngLat) => `${p[1].toFixed(7)},${p[0].toFixed(7)}`;

/** Bearing leaving `end` along the way — skips zero-length repeats. */
function awayBearing(coords: LngLat[], fromStart: boolean): number | null {
  if (coords.length < 2) return null;
  if (fromStart) {
    for (let i = 1; i < coords.length; i++) {
      if (metresBetween(coords[0], coords[i]) > 0.5) return bearingDeg(coords[0], coords[i]);
    }
  } else {
    const last = coords.length - 1;
    for (let i = last - 1; i >= 0; i--) {
      if (metresBetween(coords[last], coords[i]) > 0.5) return bearingDeg(coords[last], coords[i]);
    }
  }
  return null;
}

class UnionFind {
  private parent = new Map<number, number>();
  find(x: number): number {
    let p = this.parent.get(x);
    if (p === undefined) { this.parent.set(x, x); return x; }
    if (p !== x) { p = this.find(p); this.parent.set(x, p); }
    return p;
  }
  union(a: number, b: number): void {
    const ra = this.find(a), rb = this.find(b);
    if (ra !== rb) this.parent.set(ra, rb);
  }
}

export function prepareWays(ways: OverpassWay[], mode: RoadMode): PreparedWay[] {
  const out: PreparedWay[] = [];
  for (const w of ways) {
    if (w.type !== "way" || !w.tags || !Array.isArray(w.geometry) || w.geometry.length < 2) continue;
    if (typeof w.id !== "number") continue;
    if (!wayRateable(w.tags, mode)) continue;
    const coords: LngLat[] = w.geometry.filter(Boolean).map((g) => [g.lon, g.lat] as LngLat);
    if (coords.length < 2) continue;
    const { label, key } = normaliseLabel(w.tags);
    out.push({ id: w.id, tags: w.tags, highway: w.tags.highway ?? "", labelKey: key, label, coords });
  }
  return out;
}

/** The grouping rule described above. Exported for tests. */
export function groupWays(prepared: PreparedWay[]): PreparedWay[][] {
  const uf = new UnionFind();
  for (const w of prepared) uf.find(w.id);

  // --- named: same label + class, connected or within a small topology gap
  const named = prepared.filter((w) => w.labelKey !== null);
  for (let i = 0; i < named.length; i++) {
    for (let j = i + 1; j < named.length; j++) {
      const a = named[i], b = named[j];
      if (a.labelKey !== b.labelKey || a.highway !== b.highway) continue;
      const aEnds = [a.coords[0], a.coords[a.coords.length - 1]];
      const bEnds = [b.coords[0], b.coords[b.coords.length - 1]];
      const touching = aEnds.some((p) => bEnds.some((q) => nodeKey(p) === nodeKey(q)));
      const near = aEnds.some((p) => bEnds.some((q) => metresBetween(p, q) <= ENDPOINT_GAP_M));
      if (touching || near) uf.union(a.id, b.id);
    }
  }

  // --- unnamed: same class, shared node, collinear through it
  const unnamed = prepared.filter((w) => w.labelKey === null);
  const atNode = new Map<string, Array<{ id: number; bearing: number; highway: string }>>();
  for (const w of unnamed) {
    const startB = awayBearing(w.coords, true);
    const endB = awayBearing(w.coords, false);
    if (startB !== null) {
      const k = nodeKey(w.coords[0]);
      (atNode.get(k) ?? atNode.set(k, []).get(k)!).push({ id: w.id, bearing: startB, highway: w.highway });
    }
    if (endB !== null) {
      const k = nodeKey(w.coords[w.coords.length - 1]);
      (atNode.get(k) ?? atNode.set(k, []).get(k)!).push({ id: w.id, bearing: endB, highway: w.highway });
    }
  }
  for (const arms of atNode.values()) {
    if (arms.length < 2) continue;
    // Score every same-class pair by how close to straight-through it runs.
    // "Straight through" = the two away-bearings are near opposite (180°).
    const scored: Array<{ a: number; b: number; off: number }> = [];
    for (let i = 0; i < arms.length; i++) {
      for (let j = i + 1; j < arms.length; j++) {
        if (arms[i].id === arms[j].id) continue;
        if (arms[i].highway !== arms[j].highway) continue;
        const off = Math.abs(180 - angleBetween(arms[i].bearing, arms[j].bearing));
        scored.push({ a: arms[i].id, b: arms[j].id, off });
      }
    }
    if (scored.length === 0) continue;
    scored.sort((x, y) => x.off - y.off);
    const best = scored[0];
    if (best.off > COLLINEAR_TOLERANCE_DEG) continue;           // nothing runs through
    const runnerUp = scored[1];
    if (runnerUp && runnerUp.off - best.off < COLLINEAR_MARGIN_DEG) continue; // real junction
    uf.union(best.a, best.b);
  }

  const groups = new Map<number, PreparedWay[]>();
  for (const w of prepared) {
    const root = uf.find(w.id);
    (groups.get(root) ?? groups.set(root, []).get(root)!).push(w);
  }
  return [...groups.values()];
}

// --- ranking ---------------------------------------------------------------

export function rankCandidates(point: LngLat, groups: PreparedWay[][]): RoadCandidate[] {
  const out: RoadCandidate[] = [];
  for (const group of groups) {
    let best: { d: number; a: LngLat; b: LngLat; snapped: LngLat; coords: LngLat[] } | null = null;
    for (const w of group) {
      for (let i = 1; i < w.coords.length; i++) {
        const a = w.coords[i - 1], b = w.coords[i];
        const { snapped, distM } = projectPointToEdge(point, a, b);
        if (!best || distM < best.d) best = { d: distM, a, b, snapped, coords: w.coords };
      }
    }
    if (!best || best.d > MAX_CANDIDATE_DIST_M) continue;
    const head = group[0];
    const highway = head.highway;
    const weight = CLASS_WEIGHT[highway] ?? 1.5;
    const bearing = bearingDeg(best.a, best.b);
    out.push({
      groupId: group.map((w) => w.id).sort((x, y) => x - y).join("+"),
      displayName: head.label ?? `Unnamed ${highway.replace(/_/g, " ")}`,
      label: head.label,
      highway,
      wayIds: group.map((w) => w.id),
      distM: best.d,
      edgeA: best.a,
      edgeB: best.b,
      snapped: best.snapped,
      representative: [(best.a[0] + best.b[0]) / 2, (best.a[1] + best.b[1]) / 2],
      segmentRef: polylineSegmentKey(best.a, best.b),
      bearingDeg: bearing,
      geometry: best.coords,
      score: best.d * weight,
    });
  }
  out.sort((a, b) => a.score - b.score);
  return disambiguateUnnamed(out);
}

/** When two unnamed groups of the same class both survive, the picker must not
 *  show two identical rows. Suffix each with the axis its matched edge runs on. */
export function disambiguateUnnamed(candidates: RoadCandidate[]): RoadCandidate[] {
  const counts = new Map<string, number>();
  for (const c of candidates) {
    if (c.label !== null) continue;
    counts.set(c.displayName, (counts.get(c.displayName) ?? 0) + 1);
  }
  return candidates.map((c) => {
    if (c.label !== null || (counts.get(c.displayName) ?? 0) < 2) return c;
    return { ...c, displayName: `${c.displayName} (runs ${axisLabel(c.bearingDeg)})` };
  });
}

// --- the confidence rule ---------------------------------------------------

export function assessConfidence(
  candidates: RoadCandidate[],
  accuracyM: number | null,
  opts: { userPlacedPoint?: boolean } = {},
  // `reason?: undefined` on the success arm is deliberate. tsconfig.app.json
  // sets "strict": false, and without strictNullChecks TypeScript will not
  // narrow this union through a ternary — so `verdict.reason` at the one call
  // site below would be an error on a bare `{ ok: true }`. Declaring the
  // absent property keeps the property readable in every branch while still
  // typing it as undefined whenever ok is true.
): { ok: true; reason?: undefined } | { ok: false; reason: AmbiguityReason } {
  // Gate 1 — accuracy. Skipped when the user pointed at the spot themselves:
  // there is no fix to judge. A MISSING accuracy fails rather than passes.
  if (!opts.userPlacedPoint) {
    if (accuracyM === null || !Number.isFinite(accuracyM)) return { ok: false, reason: "accuracy" };
    if (accuracyM > MAX_CONTRIB_ACCURACY_M) return { ok: false, reason: "accuracy" };
  }
  // Gate 2 — proximity.
  const first = candidates[0];
  if (!first || first.distM > MAX_SNAP_DIST_M) return { ok: false, reason: "distance" };
  // Gate 3 — separation, measured between GROUPS.
  const second = candidates[1];
  if (second) {
    const farEnough = second.distM >= first.distM * SEPARATION_RATIO ||
                      second.distM - first.distM >= SEPARATION_ABS_M;
    if (!farEnough) return { ok: false, reason: "separation" };
  }
  return { ok: true };
}

// --- the network call ------------------------------------------------------

/**
 * Resolve a point to a road. Never throws; never invents a road.
 *
 * `accuracyM` is the reported accuracy of the fix. Pass null when the point is
 * a deliberate map tap and set `userPlacedPoint`.
 */
export async function resolveRoadAt(
  point: LngLat,
  mode: RoadMode,
  opts: { accuracyM?: number | null; userPlacedPoint?: boolean; timeoutMs?: number } = {},
): Promise<RoadResolution> {
  const accuracyM = opts.accuracyM ?? null;
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { status: "no_data", reason: "offline" };
  }
  let elements: OverpassWay[] = [];
  try {
    const [lng, lat] = point;
    const tiles = [{
      south: lat - TILE_PAD_DEG, west: lng - TILE_PAD_DEG,
      north: lat + TILE_PAD_DEG, east: lng + TILE_PAD_DEG,
    }];
    const { supabase } = await import("@/integrations/supabase/client");
    const { raceTimeout } = await import("@/lib/net");
    const res = await raceTimeout(
      supabase.functions.invoke("overpass", { body: { tiles, kind: "snaproads" } }),
      opts.timeoutMs ?? 10_000,
    );
    const data = res && !res.error ? res.data : null;
    elements = Array.isArray((data as { elements?: unknown[] } | null)?.elements)
      ? (data as { elements: OverpassWay[] }).elements
      : [];
  } catch {
    return { status: "no_data", reason: "error" };
  }
  if (elements.length === 0) return { status: "no_data", reason: "empty" };

  const candidates = rankCandidates(point, groupWays(prepareWays(elements, mode)))
    .slice(0, MAX_PICKER_CANDIDATES);
  if (candidates.length === 0) return { status: "no_data", reason: "empty" };

  const verdict = assessConfidence(candidates, accuracyM, { userPlacedPoint: opts.userPlacedPoint });
  return verdict.ok
    ? { status: "confident", chosen: candidates[0], candidates, accuracyM }
    : { status: "ambiguous", reason: verdict.reason, candidates, accuracyM };
}
